import { z } from 'zod';
import { Timestamp } from 'firebase/firestore';

/**
 * Fonction utilitaire pour transformer les timestamps Firestore en Date
 */
const transformFirestoreTimestamp = (val: any): Date => {
  if (val instanceof Timestamp) {
    return val.toDate();
  }
  // Gérer les valeurs null/undefined
  if (val === null || val === undefined) {
    return new Date(); // Retourner la date actuelle par défaut
  }

  if (val instanceof Date) {
    return val;
  }
  if (typeof val === 'string') {
    return new Date(val);
  }
  if (val && typeof val === 'object' && val.seconds !== undefined) {
    // Timestamp Firestore sous forme d'objet
    return new Date(val.seconds * 1000);
  }
  throw new Error(`Cannot transform timestamp: ${val}`);
};

/**
 * Schéma Zod pour les timestamps Firestore
 */
const firestoreTimestampSchema = z.any().transform(transformFirestoreTimestamp);

/**
 * Configuration Stripe Connect (workflow "béton")
 */
export const StripeConfigurationSchema = z.object({
  // === MODE DE PAIEMENT (DEPRECATED - moved to root level) ===
  // Kept for backward compatibility, prefer using root-level paymentMode
  paymentMode: z.enum(['stripe_connect', 'stripe_api_key']).optional(),

  // Configuration Stripe Connect
  stripeAccountId: z.string().optional(), // ID du compte Stripe Connect
  stripeConnectUrl: z.string().optional(), // URL d'onboarding ou de connexion
  stripeDashboardUrl: z.string().optional(), // URL du dashboard Stripe Connect
  stripeStatus: z.enum(['pending', 'active', 'suspended', 'rejected']).default('pending'),
  stripeVerified: z.boolean().default(false),

  // === Configuration Stripe API Key (mode stripe_api_key) ===
  stripeSecretKey: z.string().optional(), // Clé secrète Stripe fournie par le vendeur
  stripePublishableKey: z.string().optional(), // Clé publique Stripe pour le frontend
  stripeApiKeyWebhookSecret: z.string().optional(), // Secret webhook dédié

  // Gestion du webhook automatique
  webhookId: z.string().optional(), // ID du webhook dans Stripe
  webhookConfiguredAt: z.string().optional(), // Date de configuration du webhook (ISO string)
  lastWebhookReceivedAt: z.string().optional(), // Date de dernière réception d'un webhook (ISO string)

  // État du compte (mis à jour par webhooks)
  chargesEnabled: z.boolean().optional(), // Capacité à recevoir des paiements
  payoutsEnabled: z.boolean().optional(), // Capacité à envoyer des virements
  detailsSubmitted: z.boolean().optional(), // Détails soumis à Stripe

  // Requirements et capabilities (mis à jour par webhooks)
  requirements: z.object({
    currently_due: z.array(z.string()).optional(),
    eventually_due: z.array(z.string()).optional(),
    past_due: z.array(z.string()).optional(),
    disabled_reason: z.string().optional(),
    pending_verification: z.array(z.string()).optional(),
  }).optional(),

  capabilities: z.record(z.enum(['active', 'inactive', 'pending'])).optional(),

  // Informations business
  businessProfile: z.object({
    name: z.string().optional(),
    url: z.string().optional(),
  }).optional(),

  country: z.string().optional(),
  businessType: z.string().optional(),

  // Métadonnées de suivi
  lastWebhookUpdate: z.string().optional(), // Timestamp de la dernière mise à jour webhook

  // Configuration des commissions (optionnel - si non défini, utilise le taux du plan)
  // Le taux négocié a la priorité, sinon on utilise le taux du plan (FREEMIUM: 2.8%, PRO: 2%)
  commissionRate: z.number().min(0).max(100).optional(), // Taux négocié (optionnel)
  fixedCommission: z.number().min(0).optional(), // Frais fixes négociés (optionnel)

  // Configuration des transferts (fixes pour le modèle de plateforme - non visible dans l'UI)
  autoTransferEnabled: z.boolean().default(true), // Transferts automatiques toujours activés
  transferDelay: z.number().min(0).default(0), // Transfert direct (0 jours)
});

/**
 * Schéma de validation pour la nouvelle configuration Stripe Connect
 */
export const StripeConnectConfigSchema = z.object({
  accountId: z.string().optional(),
  status: z.enum(['pending', 'active', 'suspended', 'rejected']).default('pending'),
  chargesEnabled: z.boolean().default(false),
  payoutsEnabled: z.boolean().default(false),
  detailsSubmitted: z.boolean().default(false),
  autoTransferEnabled: z.boolean().default(true),
  transferDelay: z.number().default(0),
  onboardingUrl: z.string().optional(),
  dashboardUrl: z.string().optional(),
  configuredAt: z.string().optional(),
  lastUpdatedAt: z.string().optional(),
  paypalEnabled: z.boolean().default(false),
});

/**
 * Schéma de validation pour la nouvelle configuration Stripe API Key
 */
export const StripeApiKeyConfigSchema = z.object({
  hasSecretKey: z.boolean().default(false),
  hasWebhookSecret: z.boolean().default(false),
  publishableKey: z.string().optional(),
  secretKeyMasked: z.string().optional(),
  webhookSecretMasked: z.string().optional(),
  accountName: z.string().optional(),
  isTestMode: z.boolean().default(true),
  webhookId: z.string().optional(),
  webhookConfiguredAt: z.string().optional(),
  lastWebhookReceivedAt: z.string().optional(),
  lastWebhookEventId: z.string().optional(),
  lastWebhookEventType: z.string().optional(),
  lastWebhookTestReceivedAt: z.string().optional(),
  lastWebhookTestEventId: z.string().optional(),
  configuredAt: z.string().optional(),
  lastUpdatedAt: z.string().optional(),
  paypalEnabled: z.boolean().default(false),
});

/**
 * Schéma de validation pour la configuration PayPal
 */
export const PayPalConfigSchema = z.object({
  hasClientId: z.boolean().default(false),
  hasClientSecret: z.boolean().default(false),
  hasWebhookId: z.boolean().default(false),
  clientId: z.string().optional(),
  clientSecretMasked: z.string().optional(),
  clientSecretRef: z.string().optional(),
  isTestMode: z.boolean().default(true),
  webhookId: z.string().optional().nullable(),
  webhookConfiguredAt: z.string().optional().nullable(),
  lastWebhookReceivedAt: z.string().optional().nullable(),
  lastWebhookEventId: z.string().optional().nullable(),
  lastWebhookEventType: z.string().optional().nullable(),
  webhookError: z.string().optional().nullable(),
  accountEmail: z.string().optional().nullable(),
  merchantId: z.string().optional().nullable(),
  configuredAt: z.string().optional().nullable(),
  lastUpdatedAt: z.string().optional().nullable(),
});

/**
 * Schéma pour les autres providers futurs
 */
export const SumUpConfigSchema = z.object({
  paypalEnabled: z.boolean().default(false),
});

export const StancerConfigSchema = z.object({
  paypalEnabled: z.boolean().default(false),
});

export const KlarnaConfigSchema = z.object({
  paypalEnabled: z.boolean().default(false),
});

/**
 * Schéma de validation pour la configuration des modes de livraison SendCloud
 * VERSION SIMPLIFIÉE : On stocke uniquement sendcloudMethodId, isActive, availableCountries
 * Les autres données (name, price, etc.) sont récupérées depuis l'API SendCloud
 */
export const ShippingMethodSchema = z.object({
  sendcloudMethodId: z.number(), // ID de la méthode dans SendCloud
  isActive: z.boolean().default(true), // Mode de livraison actif ou non
  availableCountries: z.array(z.string()).default([]), // Codes pays disponibles (ISO 3166-1 alpha-2)
  customName: z.string().min(1).optional(),
  countryPrices: z.record(z.number().min(0)).optional(), // Mapping code pays -> prix
});

/**
 * Schéma de validation pour la configuration SendCloud (centralisée)
 * Les clés API sont gérées au niveau de la plateforme, pas par organisation
 */
/**
 * Schéma pour l'adresse expéditeur (from_address) - API Sendcloud v3
 * Champs obligatoires pour /shipments/announce: name, address_line_1, postal_code, city, country_code
 * @see https://sendcloud.dev/api/v3/shipments/create-and-announce-a-shipment-synchronously
 */
export const SendcloudSenderAddressSchema = z.object({
  sendcloudId: z.number().nullable().optional(), // Legacy v2
  name: z.string().min(1).optional(),
  address_line_1: z.string().min(1).optional(),
  house_number: z.string().optional(),
  postal_code: z.string().min(1).optional(),
  city: z.string().min(1).optional(),
  country_code: z.string().length(2).optional(),
  company_name: z.string().optional(),
  address_line_2: z.string().optional(),
  email: z.string().optional(),
  phone_number: z.string().optional(),
  state_province_code: z.string().optional(),
  po_box: z.string().optional(),
});

/** Une plage de jours ouverts (ex. vendredi à dimanche). */
export const MergeOpenDaysRangeSchema = z.object({
  start: z.number().min(0).max(6),
  end: z.number().min(0).max(6),
});

/**
 * Règles de fusion des commandes :
 * - Par défaut : fusion le même jour uniquement (frais de livraison une fois par jour)
 * - Si plage(s) de jours ouverts configurée(s) : fusion pendant toute la plage active
 */
export const OrderMergeConfigSchema = z.object({
  /** @deprecated Préférer mergeOpenDaysRanges pour plusieurs plages */
  mergeOpenDaysStart: z.number().min(0).max(6).nullable().optional(),
  /** @deprecated Préférer mergeOpenDaysRanges pour plusieurs plages */
  mergeOpenDaysEnd: z.number().min(0).max(6).nullable().optional(),
  /** Plusieurs plages de jours ouverts (aucune ne doit se chevaucher). */
  mergeOpenDaysRanges: z.array(MergeOpenDaysRangeSchema).optional(),
  /** Date de dernière mise à jour de cette configuration (ISO string). */
  updatedAt: z.string().optional(),
}).optional();

export const SendCloudConfigurationSchema = z.object({
  // SUPPRIMÉ: isEnabled - pas de notion d'activation, géré au niveau plateforme
  // SUPPRIMÉ: publicKey et secretKey - gérés au niveau plateforme
  // SUPPRIMÉ: defaultCountry - non utilisé
  // SUPPRIMÉ: allowedCountries - non utilisé
  shippingMethods: z.array(ShippingMethodSchema).default([]), // Modes de livraison configurés
  senderAddress: SendcloudSenderAddressSchema.optional(), // Adresse expéditeur (from_address) pour API v3
});

// ─── Multi-provider shipping (nouveau modèle) ─────────────────────────────

export const SendcloudApiKeyOrgConfigSchema = z.object({
  hasPublicKey: z.boolean().optional(),
  hasSecretKey: z.boolean().optional(),
  publicKeyMasked: z.string().optional(),
  secretKeyMasked: z.string().optional(),
  configuredAt: z.string().optional(),
  lastUpdatedAt: z.string().optional(),
});

export const ShippingMethodConfigSchema = z.object({
  id: z.string(),
  provider: z.enum(['sendcloud', 'sendcloud_api_key', 'mondial_relay']),
  carrier: z.string(),
  type: z.enum(['relay', 'locker', 'home']),
  weightRange: z.object({ min: z.number(), max: z.number() }),
  availableCountries: z.array(z.string()).default([]),
  countryPrices: z.record(z.number().min(0)).default({}),
  /** Tarifs plateforme (Sendcloud + marge), figés à l’ajout — affichage admin */
  platformCountryPrices: z.record(z.number().min(0)).optional(),
  customName: z.string().optional(),
  isActive: z.boolean().default(true),
  sendcloudMethodId: z.number().optional(),
  mondialRelayModeLiv: z.enum(['24R', '24L']).optional(),
});

export const MondialRelayOrgConfigSchema = z.object({
  enseigne: z.string(),
  apiLogin: z.string().optional(),
  /** Legacy — préférer Secret Manager via saveMondialRelaySecrets */
  apiPassword: z.string().optional(),
  customerId: z.string().optional(),
  hasApiPassword: z.boolean().optional(),
  apiPasswordMasked: z.string().optional(),
  configuredAt: z.string().optional(),
  lastUpdatedAt: z.string().optional(),
});

export const ShippingConfigurationSchema = z.object({
  methods: z.array(ShippingMethodConfigSchema).default([]),
  sendcloudApiKeyConfig: SendcloudApiKeyOrgConfigSchema.optional(),
  mondialRelayConfig: MondialRelayOrgConfigSchema.optional(),
  sendcloudEnabled: z.boolean().default(true),
  mondialRelayEnabled: z.boolean().default(false),
  /** Si false : pas de création auto au paiement — génération depuis l’admin (crédits CB uniquement pour Sendcloud). */
  autoParcelGenerationOnPayment: z.boolean().default(true),
  /** Poids par défaut d’un colis (kg) si la commande n’a pas minWeight. */
  defaultParcelWeightKg: z.number().min(0.01).max(999).optional(),
  senderAddress: SendcloudSenderAddressSchema.optional(),
  orderMergeConfig: OrderMergeConfigSchema,
});

/**
 * Types de plans disponibles
 */
export const PlanTypeSchema = z.enum(['FREEMIUM', 'PRO', 'LAUNCH_OFFER']);

/**
 * Statut de l'abonnement Stripe
 */
export const SubscriptionStatusSchema = z.enum([
  'active',
  'canceled',
  'past_due',
  'unpaid',
  'trialing',
  'incomplete',
  'incomplete_expired',
  'paused',
]);

/**
 * Schéma de validation pour la configuration d'abonnement
 */
export const SubscriptionConfigSchema = z.object({
  // Plan actif
  planType: PlanTypeSchema.default('FREEMIUM'),

  // Abonnement Stripe (pour plan PRO)
  stripeSubscriptionId: z.string().optional().nullable(),
  stripeCustomerId: z.string().optional(),
  stripePriceId: z.string().optional(),
  subscriptionStatus: SubscriptionStatusSchema.optional(),
  subscriptionCurrentPeriodEnd: firestoreTimestampSchema.optional(),
  subscriptionCancelAtPeriodEnd: z.boolean().optional(),

  // ========== ABONNEMENT PERSONNALISÉ (Super Admin) ==========
  isCustomPlan: z.boolean().optional(),
  customPlanName: z.string().optional().nullable(),
  customPrice: z.number().optional().nullable(),
  customCommissionRate: z.number().optional().nullable(),
  customFeatures: z.object({
    iaAssistant: z.boolean(),
    savChat: z.boolean(),
    savHistory: z.boolean(),
  }).optional().nullable(),
  customPlanNote: z.string().optional().nullable(),
  customPlanSetBy: z.string().optional().nullable(),
  customPlanSetAt: firestoreTimestampSchema.optional().nullable(),

  // Proposition en attente
  pendingCustomOffer: z.object({
    customPlanName: z.string(),
    customPrice: z.number(),
    customCommissionRate: z.number(),
    customFeatures: z.object({
      iaAssistant: z.boolean(),
      savChat: z.boolean(),
      savHistory: z.boolean(),
    }),
    customPlanNote: z.string().optional(),
    proposedBy: z.string(),
    proposedAt: firestoreTimestampSchema,
    status: z.enum(['pending', 'accepted', 'rejected']),
  }).optional().nullable(),
  // ===========================================================

  // Bonus de bienvenue (1000€ de CA à 0% commission CashLive)

  // CA consommé du bonus (en centimes)


  // Note: cumulativeRevenue supprimé - maintenant calculé dynamiquement depuis les commandes payées

  // Configuration IA
  iaConfig: z.object({
    enabled: z.boolean().default(false),
    creditsRemaining: z.number().default(0), // Pour FREEMIUM: crédits achetés
    creditsUsed: z.number().default(0),
    lastCreditsPurchaseDate: firestoreTimestampSchema.optional(),
  }).optional(),

  // Configuration SAV
  savConfig: z.object({
    chatEnabled: z.boolean().default(false),
    historyEnabled: z.boolean().default(false),
  }).optional(),

  // Historique
  planChangedAt: firestoreTimestampSchema.optional(),
});

/**
 * Schéma de validation pour la configuration d'organisation
 */
export const OrgaConfigurationSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  name: z.string().min(1, 'Le nom est requis'),

  // Informations de contact et adresse
  address: z.object({
    street: z.string().optional(),
    city: z.string().optional(),
    postalCode: z.string().optional(),
    country: z.string().optional(),
  }).optional(),

  // NOUVELLES SECTIONS
  // Contacts internes (non visibles par les clients)
  contacts: z.array(z.object({
    name: z.string(),
    email: z.string().optional(),
    phone: z.string().optional(),
    position: z.string().optional()
  })).default([]),

  // Service après-vente (visible par les clients)
  afterSalesService: z.object({
    isSavChatEnabled: z.boolean().default(false), // Activation du chat SAV
    contacts: z.array(z.object({
      type: z.enum(['email', 'phone', 'whatsapp']),
      value: z.string(),
      label: z.string().optional() // Optionnel : nom personnalisé (ex: "Service Commercial")
    })).default([]),
    description: z.record(z.string()).optional(), // Description multilingue
  }).default({}),

  // Réseaux sociaux (publics)
  socials: z.record(z.string()).default({}),

  // CHAMPS EXISTANTS (CONSERVÉS POUR RÉTROCOMPATIBILITÉ)
  phone: z.string().optional(),
  email: z.string().email().optional(),
  website: z.string().optional(),
  industry: z.string().optional(), // Ajout du champ secteur d'activité
  whatsappNumber: z.string().optional(),
  instagramPage: z.string().optional(),
  facebookPage: z.string().optional(),
  tiktokPage: z.string().optional(),

  // Informations légales (mentions légales, CGV, politique de confidentialité)
  legalInfo: z.object({
    companyName: z.string().optional(), // Nom légal de l'entreprise
    legalId: z.string().optional(), // Identifiant légal / Numéro d'enregistrement (SIRET, VAT, Company Number, etc.)
    vatNumber: z.string().optional(), // Numéro de TVA (optionnel)
    address: z.object({
      street: z.string().optional(),
      city: z.string().optional(),
      postalCode: z.string().optional(),
      country: z.string().optional(),
    }).optional(),
  }).optional(),

  // Configuration Stripe
  stripeConfig: StripeConfigurationSchema.optional(),

  // Configuration SendCloud (legacy - conservée pour rétrocompatibilité)
  sendCloudConfig: SendCloudConfigurationSchema.optional(),

  // Configuration multi-provider shipping (nouveau modèle)
  shippingConfig: ShippingConfigurationSchema.optional(),

  // Configuration d'abonnement CashLive (plan, bonus, CA)
  subscriptionConfig: SubscriptionConfigSchema.optional(),

  // Configuration du recrutement
  recruitment: z.object({
    enabled: z.boolean().default(false),
    description: z.object({
      fr: z.string().optional(),
      en: z.string().optional(),
      es: z.string().optional(),
    }).optional(),
  }).optional(),

  // Personnalisation des couleurs et styles pour les pages de commande
  personalization: z.object({
    // Logo et description de l'organisation
    logo: z.string().optional(),
    description: z.string().optional(),
    // Configuration du format du champ modèle produit
    productModelConfig: z.object({
      format: z.enum(['structured', 'free', 'custom']).default('structured'), // structured = Type + Taille, free = champ libre, custom = format personnalisé
      modelPlaceholder: z.string().optional(), // Placeholder pour le champ libre (format 'free')
      typePlaceholder: z.string().optional(), // Placeholder pour le champ Type (format structuré)
      sizePlaceholder: z.string().optional(), // Placeholder pour le champ Taille (format structuré)
      // Format personnalisé (custom) : liste de champs configurables
      customFields: z.array(z.object({
        key: z.string(), // Clé unique du champ (ex: 'type', 'size', 'color', etc.)
        label: z.string(), // Label affiché à l'utilisateur
        placeholder: z.string().optional(), // Placeholder du champ
        type: z.enum(['text', 'textarea', 'number', 'email', 'tel', 'url', 'date']).default('text'), // Type d'input HTML
        required: z.boolean().default(false), // Champ requis ou non
      })).optional(), // Liste des champs personnalisés (format 'custom')
    }).optional(),
    // Background du layout
    layout: z.object({
      background: z.string().optional(), // Couleur, dégradé (ex: "linear-gradient(...)") ou URL d'image (ex: "url(...)")
      backgroundType: z.enum(['color', 'gradient', 'image']).optional().default('color'), // Type de background
    }).optional(),
    // Cartes
    card: z.object({
      background: z.string().optional(), // Couleur de fond des cartes
      border: z.string().optional(), // Couleur de bordure
      borderRadius: z.string().optional(), // Border radius (ex: "rounded-lg", "rounded-xl")
      shadow: z.string().optional(), // Shadow (ex: "shadow-sm", "shadow-lg")
    }).optional(),
    // Inputs
    input: z.object({
      background: z.string().optional(), // Couleur de fond
      border: z.string().optional(), // Couleur de bordure
      focusBorder: z.string().optional(), // Couleur de bordure au focus
      focusRing: z.string().optional(), // Couleur du ring au focus
      text: z.string().optional(), // Couleur du texte
      placeholder: z.string().optional(), // Couleur du placeholder
      borderRadius: z.string().optional(), // Border radius
    }).optional(),
    // Boutons
    button: z.object({
      primary: z.string().optional(), // Couleur de fond du bouton primaire ou gradient (ex: "linear-gradient(to right, #9333EA, #EC4899)")
      primaryText: z.string().optional(), // Couleur du texte du bouton primaire
      primaryHover: z.string().optional(), // Couleur au survol ou gradient
      disabledBackground: z.string().optional(), // Couleur de fond du bouton désactivé
      disabledText: z.string().optional(), // Couleur du texte du bouton désactivé
      borderRadius: z.string().optional(), // Border radius
    }).optional(),
    // Textes
    text: z.object({
      primary: z.string().optional(), // Couleur du texte principal
      secondary: z.string().optional(), // Couleur du texte secondaire
      heading: z.string().optional(), // Couleur des titres
    }).optional(),
    // Badges/Labels
    badge: z.object({
      background: z.string().optional(), // Couleur de fond
      text: z.string().optional(), // Couleur du texte
      borderRadius: z.string().optional(), // Border radius
    }).optional(),
    // Icônes
    icon: z.object({
      primary: z.string().optional(), // Couleur principale des icônes
      secondary: z.string().optional(), // Couleur secondaire des icônes
    }).optional(),
    // Annonce informative (Bannière ou message important au client)
    informativeAnnouncement: z.string().optional(),
  }).optional(),

  isActive: z.boolean().default(true),
  isStripeEnabled: z.boolean().default(false),

  // Mode de paiement (provider-agnostic)
  // Permet de sélectionner le provider de paiement actif indépendamment de Stripe
  paymentMode: z.enum(['stripe_connect', 'stripe_api_key', 'sumup', 'paypal', 'stancer', 'klarna', 'none']).default('none'),

  // NOUVELLES CONFIGURATIONS MULTI-PROVIDER
  stripeConnectConfig: StripeConnectConfigSchema.optional(),
  stripeApiKeyConfig: StripeApiKeyConfigSchema.optional(),
  paypalConfig: PayPalConfigSchema.optional(),
  sumupConfig: SumUpConfigSchema.optional(),
  stancerConfig: StancerConfigSchema.optional(),
  klarnaConfig: KlarnaConfigSchema.optional(),


  // Unified app credits (for shipping and AI)
  creditsBalance: z.object({
    balanceCents: z.number().default(0),
    totalPurchasedCents: z.number().default(0),
    totalUsedCents: z.number().default(0),
    totalUsedForShippingCents: z.number().default(0),
    totalUsedForAICents: z.number().default(0),
    updatedAt: firestoreTimestampSchema.optional(),
  }).optional(),

  // Métadonnées
  createdAt: firestoreTimestampSchema,
  updatedAt: firestoreTimestampSchema,
});

/**
 * Interface TypeScript pour la configuration Stripe (workflow "béton")
 */
export interface StripeConfiguration {
  // === MODE DE PAIEMENT (DEPRECATED - moved to OrgaConfiguration root level) ===
  // Kept for backward compatibility, prefer using OrgaConfiguration.paymentMode
  paymentMode?: 'stripe_connect' | 'stripe_api_key';

  // Configuration Stripe Connect
  stripeAccountId?: string; // ID du compte Stripe Connect
  stripeConnectUrl?: string; // URL d'onboarding ou de connexion
  stripeDashboardUrl?: string; // URL du dashboard Stripe Connect
  stripeStatus: 'pending' | 'active' | 'suspended' | 'rejected';
  stripeVerified: boolean;

  // === Configuration Stripe API Key (mode stripe_api_key) ===
  // LEGACY: valeurs directes (DEPRECATED - migrer vers Secret Manager)
  stripeSecretKey?: string; // DEPRECATED: Clé secrète Stripe
  stripePublishableKey?: string; // Clé publique Stripe pour le frontend
  stripeApiKeyWebhookSecret?: string; // DEPRECATED: Secret webhook

  // === NOUVEAUX CHAMPS - Métadonnées de secrets (Secret Manager) ===
  // Les vraies valeurs sont dans Google Cloud Secret Manager
  hasSecretKey?: boolean;
  hasPublishableKey?: boolean;
  hasWebhookSecret?: boolean;
  secretKeyMasked?: string;      // ex: "rk_test_51IA...ETcP"
  publishableKeyMasked?: string; // ex: "pk_test_51IA...PhFJ"
  webhookSecretMasked?: string;  // ex: "whsec_aI72...xzPb"
  secretKeyRef?: string;
  publishableKeyRef?: string;
  webhookSecretRef?: string;
  accountName?: string;
  isTestMode?: boolean;
  configuredAt?: string;

  // Gestion du webhook automatique
  webhookId?: string; // ID du webhook dans Stripe
  webhookConfiguredAt?: string; // Date de configuration du webhook (ISO string)
  lastWebhookReceivedAt?: string; // Date de dernière réception d'un webhook (ISO string)
  lastWebhookTestReceivedAt?: string;
  lastWebhookTestEventId?: string;

  // État du compte (mis à jour par webhooks)
  chargesEnabled?: boolean; // Capacité à recevoir des paiements
  payoutsEnabled?: boolean; // Capacité à envoyer des virements
  detailsSubmitted?: boolean; // Détails soumis à Stripe

  // Requirements et capabilities (mis à jour par webhooks)
  requirements?: {
    currently_due?: string[];
    eventually_due?: string[];
    past_due?: string[];
    disabled_reason?: string;
    pending_verification?: string[];
  };

  capabilities?: Record<string, 'active' | 'inactive' | 'pending'>;

  // Informations business
  businessProfile?: {
    name?: string;
    url?: string;
  };

  country?: string;
  businessType?: string;

  // Métadonnées de suivi
  lastWebhookUpdate?: string; // Timestamp de la dernière mise à jour webhook

  // Configuration des commissions (optionnel - si non défini, utilise le taux du plan)
  commissionRate?: number; // Taux de commission négocié
  fixedCommission?: number; // Commission fixe négociée

  // Configuration des transferts (fixes pour le modèle de plateforme - non visible dans l'UI)
  autoTransferEnabled: boolean; // Transferts automatiques
  transferDelay: number; // Délai de transfert
}

/**
 * Interface TypeScript pour un mode de livraison SendCloud
 * VERSION SIMPLIFIÉE : On stocke uniquement les données minimales
 * Les autres données (name, price, etc.) sont récupérées depuis l'API SendCloud
 */
export interface ShippingMethod {
  sendcloudMethodId: number; // ID de la méthode dans SendCloud
  isActive: boolean; // Mode de livraison actif ou non
  availableCountries: string[]; // Codes pays disponibles (ISO 3166-1 alpha-2)
  isHomeDelivery?: boolean; // Si c'est une livraison à domicile
  customName?: string;
  countryPrices?: Record<string, number>;
}

/**
 * Interface TypeScript pour la configuration SendCloud (centralisée)
 * Les clés API sont gérées au niveau de la plateforme, pas par organisation
 */
/** Adresse expéditeur pour Sendcloud v3 (from_address) */
export interface SendcloudSenderAddress {
  sendcloudId?: number | null;
  name?: string;
  address_line_1?: string;
  house_number?: string;
  postal_code?: string;
  city?: string;
  country_code?: string;
  company_name?: string;
  address_line_2?: string;
  email?: string;
  phone_number?: string;
  state_province_code?: string;
  po_box?: string;
}

export interface MergeOpenDaysRange {
  start: number;
  end: number;
}

export interface OrderMergeConfig {
  /** @deprecated Préférer mergeOpenDaysRanges */
  mergeOpenDaysStart?: number | null;
  /** @deprecated Préférer mergeOpenDaysRanges */
  mergeOpenDaysEnd?: number | null;
  /** Plusieurs plages de jours ouverts (sans chevauchement). */
  mergeOpenDaysRanges?: MergeOpenDaysRange[];
  /** Date de dernière mise à jour de cette configuration (ISO string). */
  updatedAt?: string;
}

export interface SendCloudConfiguration {
  // SUPPRIMÉ: isEnabled - pas de notion d'activation, géré au niveau plateforme
  // SUPPRIMÉ: publicKey et secretKey - gérés au niveau plateforme
  // SUPPRIMÉ: defaultCountry - non utilisé
  // SUPPRIMÉ: allowedCountries - non utilisé
  shippingMethods: ShippingMethod[]; // Modes de livraison configurés
  senderAddress?: SendcloudSenderAddress; // Adresse expéditeur (from_address) pour API v3
}

// ─── Multi-provider shipping interfaces (nouveau modèle) ──────────────────

export interface SendcloudApiKeyOrgConfig {
  hasPublicKey?: boolean;
  hasSecretKey?: boolean;
  publicKeyMasked?: string;
  secretKeyMasked?: string;
  configuredAt?: string;
  lastUpdatedAt?: string;
}

export interface ShippingMethodConfig {
  id: string;
  provider: 'sendcloud' | 'sendcloud_api_key' | 'mondial_relay';
  carrier: string;
  type: 'relay' | 'locker' | 'home';
  weightRange: { min: number; max: number };
  availableCountries: string[];
  countryPrices: Record<string, number>;
  /** Tarifs plateforme Sendcloud (avec marge), pour affichage / référence admin */
  platformCountryPrices?: Record<string, number>;
  customName?: string;
  isActive: boolean;
  sendcloudMethodId?: number;
  mondialRelayModeLiv?: '24R' | '24L';
}

export interface MondialRelayOrgConfig {
  enseigne: string;
  apiLogin?: string;
  /** Legacy — mot de passe dans Google Secret Manager pour les configs à jour */
  apiPassword?: string;
  customerId?: string;
  hasApiPassword?: boolean;
  apiPasswordMasked?: string;
  configuredAt?: string;
  lastUpdatedAt?: string;
}

export interface ShippingConfiguration {
  methods: ShippingMethodConfig[];
  sendcloudApiKeyConfig?: SendcloudApiKeyOrgConfig;
  mondialRelayConfig?: MondialRelayOrgConfig;
  sendcloudEnabled?: boolean;
  mondialRelayEnabled?: boolean;
  /** Désactivé → colis à générer manuellement depuis l’admin. */
  autoParcelGenerationOnPayment?: boolean;
  defaultParcelWeightKg?: number;
  senderAddress?: SendcloudSenderAddress;
  orderMergeConfig?: OrderMergeConfig;
}

/**
 * Types de plans disponibles
 */
export type PlanType = 'FREEMIUM' | 'PRO' | 'LAUNCH_OFFER';

/**
 * Statut de l'abonnement Stripe
 */
export type SubscriptionStatus =
  | 'active'
  | 'canceled'
  | 'past_due'
  | 'unpaid'
  | 'trialing'
  | 'incomplete'
  | 'incomplete_expired'
  | 'paused';

/**
 * Interface TypeScript pour la configuration d'abonnement CashLive
 */
export interface SubscriptionConfig {
  // Plan actif
  planType: PlanType;

  // Abonnement Stripe (pour plan PRO)
  stripeSubscriptionId?: string | null;
  stripeCustomerId?: string;
  stripePriceId?: string;
  subscriptionStatus?: SubscriptionStatus;
  subscriptionCurrentPeriodEnd?: Date;
  subscriptionCancelAtPeriodEnd?: boolean;

  // ========== ABONNEMENT PERSONNALISÉ (Super Admin) ==========
  isCustomPlan?: boolean;
  customPlanName?: string | null;
  customPrice?: number | null;
  customCommissionRate?: number | null;
  customStripePriceId?: string | null;
  customFeatures?: {
    iaAssistant: boolean;
    savChat: boolean;
    savHistory: boolean;
  } | null;
  customPlanNote?: string | null;
  customPlanSetBy?: string | null;
  customPlanSetAt?: Date | null;
  customPlanExpiresAt?: Date | null;

  // Proposition d'abonnement personnalisé (en attente d'acceptation par le client)
  pendingCustomOffer?: {
    customPlanName: string;
    customPrice: number;
    customCommissionRate: number;
    customFeatures: {
      iaAssistant: boolean;
      savChat: boolean;
      savHistory: boolean;
    };
    customPlanNote?: string;
    proposedBy: string;
    proposedAt: Date;
    expiresAt?: Date | null;
    status: 'pending' | 'accepted' | 'rejected';
  } | null;
  // ===========================================================

  // Bonus de bienvenue (1000€ de CA à 0% commission CashLive)

  // CA consommé du bonus (en centimes)

  // Note: cumulativeRevenue supprimé - maintenant calculé dynamiquement via useCumulativeRevenue()

  // Configuration IA
  iaConfig?: {
    enabled: boolean;
    creditsRemaining: number; // Pour FREEMIUM: crédits achetés
    creditsUsed: number;
    lastCreditsPurchaseDate?: Date;
  };

  // Configuration SAV
  savConfig?: {
    chatEnabled: boolean;
    historyEnabled: boolean;
  };

  // Historique
  planChangedAt?: Date;
}

/**
 * Interface TypeScript pour la configuration d'organisation
 */
export interface OrgaConfiguration {
  id: string;
  organizationId: string;
  name: string;

  // Informations de contact et adresse
  address?: {
    street?: string;
    city?: string;
    postalCode?: string;
    country?: string;
  };

  // NOUVELLES SECTIONS
  contacts: Array<{
    name: string;
    email?: string;
    phone?: string;
    position?: string;
  }>;
  afterSalesService: {
    isSavChatEnabled: boolean;
    contacts: Array<{
      type: 'email' | 'phone' | 'whatsapp';
      value: string;
      label?: string;
    }>;
    description?: Record<string, string>;
  };
  socials: Record<string, string>;

  // CHAMPS EXISTANTS (CONSERVÉS POUR RÉTROCOMPATIBILITÉ)
  phone?: string;
  email?: string;
  website?: string;
  industry?: string; // Ajout du champ secteur d'activité
  whatsappNumber?: string;
  instagramPage?: string;
  facebookPage?: string;
  tiktokPage?: string;

  // Informations légales
  legalInfo?: {
    companyName?: string; // Nom légal de l'entreprise
    legalId?: string; // Identifiant légal / Numéro d'enregistrement
    vatNumber?: string; // Numéro de TVA (optionnel)
    address?: {
      street?: string;
      city?: string;
      postalCode?: string;
      country?: string;
    };
  };

  // Configuration Stripe
  stripeConfig?: StripeConfiguration;

  // Configuration SendCloud (legacy)
  sendCloudConfig?: SendCloudConfiguration;

  // Multi-provider shipping (nouveau modèle)
  shippingConfig?: ShippingConfiguration;

  // Configuration d'abonnement CashLive
  subscriptionConfig?: SubscriptionConfig;

  // Configuration du recrutement
  recruitment?: {
    enabled: boolean;
    description?: {
      fr?: string;
      en?: string;
      es?: string;
    };
  };

  // Personnalisation des couleurs et styles pour les pages de commande
  personalization?: {
    // Logo et description de l'organisation
    logo?: string;
    description?: string;
    // Configuration du format du champ modèle produit
    productModelConfig?: {
      format: 'structured' | 'free' | 'custom'; // structured = Type + Taille, free = champ libre, custom = format personnalisé
      modelPlaceholder?: string; // Placeholder pour le champ libre (format 'free')
      typePlaceholder?: string; // Placeholder pour le champ Type (format structuré)
      sizePlaceholder?: string; // Placeholder pour le champ Taille (format structuré)
      // Format personnalisé (custom) : liste de champs configurables
      customFields?: Array<{
        key: string; // Clé unique du champ (ex: 'type', 'size', 'color', etc.)
        label: string; // Label affiché à l'utilisateur
        placeholder?: string; // Placeholder du champ
        type: 'text' | 'textarea' | 'number' | 'email' | 'tel' | 'url' | 'date'; // Type d'input HTML
        required: boolean; // Champ requis ou non
      }>;
    };
    card?: {
      background?: string;
      border?: string;
      borderRadius?: string;
      shadow?: string;
    };
    input?: {
      background?: string;
      border?: string;
      focusBorder?: string;
      focusRing?: string;
      text?: string;
      placeholder?: string;
      borderRadius?: string;
    };
    button?: {
      primary?: string;
      primaryText?: string;
      primaryHover?: string;
      disabledBackground?: string;
      disabledText?: string;
      borderRadius?: string;
    };
    text?: {
      primary?: string;
      secondary?: string;
      heading?: string;
    };
    badge?: {
      background?: string;
      text?: string;
      borderRadius?: string;
    };
    icon?: {
      primary?: string;
      secondary?: string;
    };
    informativeAnnouncement?: string;
  };

  // Configuration de l'organisation
  isActive: boolean;
  isStripeEnabled: boolean;

  // Mode de paiement actif (provider-agnostic)
  // 'stripe_connect' | 'stripe_api_key' | 'sumup' | 'paypal' | 'stancer' | 'klarna' | 'none'
  paymentMode: PaymentMode;

  // =====================================================
  // NOUVELLES CONFIGURATIONS MULTI-PROVIDER
  // =====================================================

  // Configuration Stripe Connect (compte connecté via CashLive)
  stripeConnectConfig?: StripeConnectConfig;

  // Configuration Stripe API Key (compte propre du vendeur)
  stripeApiKeyConfig?: StripeApiKeyConfig;

  // Configuration PayPal (compte propre du vendeur)
  paypalConfig?: PayPalConfig;

  // Configuration SumUp
  sumupConfig?: SumUpConfig;

  // Configuration Stancer
  stancerConfig?: StancerConfig;

  // Configuration Klarna
  klarnaConfig?: KlarnaConfig;

  // Configuration des commissions (commune à tous les providers)


  // Unified app credits (for shipping and AI)
  creditsBalance?: {
    balanceCents: number;
    totalPurchasedCents: number;
    totalUsedCents: number;
    totalUsedForShippingCents: number;
    totalUsedForAICents: number;
    updatedAt?: Date;
  };

  // Métadonnées
  createdAt: Date;
  updatedAt: Date;
}

// ============================================================================
// NOUVELLES INTERFACES MULTI-PROVIDER
// ============================================================================

/**
 * Types de modes de paiement supportés
 */
export type PaymentMode = 'stripe_connect' | 'stripe_api_key' | 'sumup' | 'paypal' | 'stancer' | 'klarna' | 'none';

/**
 * Configuration Stripe Connect
 * Utilisé quand le vendeur utilise un compte connecté via CashLive
 */
export interface StripeConnectConfig {
  // Identifiant du compte Stripe Connect
  accountId?: string;

  // Statut du compte
  status: 'pending' | 'active' | 'suspended' | 'rejected';
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  detailsSubmitted: boolean;

  // Configuration des transferts
  autoTransferEnabled: boolean;
  transferDelay: number; // jours

  // URLs
  onboardingUrl?: string;
  dashboardUrl?: string;

  // Métadonnées
  configuredAt?: string;
  lastUpdatedAt?: string;

  // PayPal extension
  paypalEnabled: boolean;
}

/**
 * Configuration Stripe API Key
 * Utilisé quand le vendeur utilise son propre compte Stripe
 * 
 * NOTE: Les clés SECRÈTES (secretKey, webhookSecret) sont dans Secret Manager
 *       La clé PUBLIQUE (publishableKey) est stockée directement car elle n'est pas secrète
 */
export interface StripeApiKeyConfig {
  // Indicateurs pour les clés dans Secret Manager
  hasSecretKey: boolean;       // Clé secrète (sk_/rk_) dans Secret Manager
  hasWebhookSecret: boolean;   // Webhook secret (whsec_) dans Secret Manager

  // Clé publique - stockée directement (pas secrète, exposée au frontend)
  publishableKey?: string;     // pk_test_xxx ou pk_live_xxx

  // Versions masquées pour affichage UI (pour les clés secrètes)
  secretKeyMasked?: string;      // ex: "rk_test_51IA...ETcP"
  webhookSecretMasked?: string;  // ex: "whsec_aI72...xzPb"

  // Informations du compte
  accountName?: string;
  isTestMode: boolean;

  // Configuration webhook
  webhookId?: string;
  webhookConfiguredAt?: string;
  lastWebhookReceivedAt?: string;
  lastWebhookEventId?: string;
  lastWebhookEventType?: string;
  lastWebhookTestReceivedAt?: string;
  lastWebhookTestEventId?: string;

  // Métadonnées
  configuredAt?: string;
  lastUpdatedAt?: string;

  // PayPal extension
  paypalEnabled: boolean;
}

/**
 * Configuration PayPal
 * Utilisé quand le vendeur utilise son propre compte PayPal
 * 
 * NOTE: Les clés SECRÈTES (clientSecret) sont dans Secret Manager
 *       Le Client ID est stocké directement car il est semi-public
 */
export interface PayPalConfig {
  // Indicateurs pour les clés dans Secret Manager
  hasClientId: boolean;        // Client ID PayPal configuré
  hasClientSecret: boolean;    // Client Secret dans Secret Manager
  hasWebhookId: boolean;       // Webhook configuré

  // Client ID - stocké directement (semi-public)
  clientId?: string;

  // Versions masquées pour affichage UI
  clientSecretMasked?: string;

  // Références vers Secret Manager
  clientSecretRef?: string;

  // Mode sandbox ou live
  isTestMode: boolean;

  // Configuration webhook
  webhookId?: string | null;
  webhookConfiguredAt?: string | null;
  lastWebhookReceivedAt?: string | null;
  lastWebhookEventId?: string | null;
  lastWebhookEventType?: string | null;

  // Informations du compte
  accountEmail?: string | null;
  merchantId?: string | null;

  // Métadonnées
  configuredAt?: string | null;
  lastUpdatedAt?: string | null;
}

/**
 * Configuration SumUp
 */
export interface SumUpConfig {
  paypalEnabled: boolean;
}

/**
 * Configuration Stancer
 */
export interface StancerConfig {
  paypalEnabled: boolean;
}

/**
 * Configuration Klarna
 */
export interface KlarnaConfig {
  paypalEnabled: boolean;
}

/**
/**
 * Interface pour créer une nouvelle configuration
 */
export interface CreateOrgaConfigurationRequest {
  organizationId: string;
  name: string;
  primaryColor?: string;
  secondaryColor?: string;
  address?: {
    street?: string;
    city?: string;
    postalCode?: string;
    country?: string;
  };
  // NOUVELLES SECTIONS
  contacts?: Array<{
    name: string;
    email?: string;
    phone?: string;
    position?: string;
  }>;
  afterSalesService?: {
    isSavChatEnabled?: boolean;
    contacts?: Array<{
      type: 'email' | 'phone' | 'whatsapp';
      value: string;
      label?: string;
    }>;
    description?: Record<string, string>;
  };
  socials?: Record<string, string>;

  phone?: string;
  email?: string;
  website?: string;
  industry?: string; // Ajout du champ secteur d'activité
  whatsappNumber?: string;
  instagramPage?: string;
  facebookPage?: string;
  tiktokPage?: string;
  stripeConfig?: Partial<StripeConfiguration>;
  sendCloudConfig?: Partial<SendCloudConfiguration>;
}

/**
 * Interface pour mettre à jour une configuration
 */
export interface UpdateOrgaConfigurationRequest {
  name?: string;
  primaryColor?: string;
  secondaryColor?: string;
  address?: {
    street?: string;
    city?: string;
    postalCode?: string;
    country?: string;
  };
  // NOUVELLES SECTIONS
  contacts?: Array<{
    name: string;
    email?: string;
    phone?: string;
    position?: string;
  }>;
  afterSalesService?: {
    isSavChatEnabled?: boolean;
    contacts?: Array<{
      type: 'email' | 'phone' | 'whatsapp';
      value: string;
      label?: string;
    }>;
    description?: Record<string, string>;
  };
  socials?: Record<string, string>;

  phone?: string;
  email?: string;
  website?: string;
  industry?: string; // Ajout du champ secteur d'activité
  whatsappNumber?: string;
  instagramPage?: string;
  facebookPage?: string;
  tiktokPage?: string;

  // NOUVELLES CONFIGURATIONS PAIEMENT
  paymentMode?: PaymentMode;
  stripeConnectConfig?: Partial<StripeConnectConfig>;
  stripeApiKeyConfig?: Partial<StripeApiKeyConfig>;
  paypalConfig?: Partial<PayPalConfig>;
  sumupConfig?: Partial<SumUpConfig>;
  stancerConfig?: Partial<StancerConfig>;
  klarnaConfig?: Partial<KlarnaConfig>;


  stripeConfig?: Partial<StripeConfiguration>;
  sendCloudConfig?: Partial<SendCloudConfiguration>;
  recruitment?: {
    enabled: boolean;
    description?: {
      fr?: string;
      en?: string;
      es?: string;
    };
  };
  personalization?: {
    logo?: string;
    description?: string;
    productModelConfig?: {
      format?: 'structured' | 'free' | 'custom';
      modelPlaceholder?: string;
      typePlaceholder?: string;
      sizePlaceholder?: string;
      customFields?: Array<{
        key: string;
        label: string;
        placeholder?: string;
        type?: 'text' | 'textarea' | 'number' | 'email' | 'tel' | 'url' | 'date';
        required?: boolean;
      }>;
    };
    card?: {
      background?: string;
      border?: string;
      borderRadius?: string;
      shadow?: string;
    };
    input?: {
      background?: string;
      border?: string;
      focusBorder?: string;
      focusRing?: string;
      text?: string;
      placeholder?: string;
      borderRadius?: string;
    };
    button?: {
      primary?: string;
      primaryText?: string;
      primaryHover?: string;
      disabledBackground?: string;
      disabledText?: string;
      borderRadius?: string;
    };
    text?: {
      primary?: string;
      secondary?: string;
      heading?: string;
    };
    badge?: {
      background?: string;
      text?: string;
      borderRadius?: string;
    };
    icon?: {
      primary?: string;
      secondary?: string;
    };
    informativeAnnouncement?: string;
  };
  isActive?: boolean;
  isStripeEnabled?: boolean;
}